import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip from "@hyro-components/Tooltip";
import palette from "src/theme/palette";
import {
  ALL_CHANNELS_USAGE_LABEL,
  buildPlanSubtitle,
  CHANNEL_META,
  QUOTA_NOT_STARTED_SUBTITLE,
  UNLIMITED_LIMIT_LABEL,
  UNLIMITED_PLAN_SUBTITLE,
  USAGE_BAR_COLORS,
  USAGE_BAR_MIN_SEGMENT_PX
} from "./SubscriptionTab.consts";

const Track = styled(Stack)({
  width: "100%",
  height: 16,
  borderRadius: 2,
  overflow: "hidden",
  flexDirection: "row",
  backgroundColor: USAGE_BAR_COLORS.track
});

const LegendDot = styled(Box)(({ color }) => ({
  width: 10,
  height: 10,
  borderRadius: "50%",
  backgroundColor: color,
  flexShrink: 0
}));

const tooltipFor = (label, color, count, description) => (
  <Stack gap={1}>
    <Stack direction="row" alignItems="center" gap={1}>
      <LegendDot color={color} />
      <Typography variant="tooltip" color="text.800">{label}</Typography>
    </Stack>
    <Typography variant="tooltip" color="text.700">{count.toLocaleString()} Interactions</Typography>
    <Typography variant="tooltip" color="text.600" sx={{ maxWidth: 145 }}>{description}</Typography>
  </Stack>
);

const channelValue = (entry, interactionDefinition) => {
  if (!entry) return 0;
  return interactionDefinition === "All" ? entry.total : entry.engaged;
};

const QuotaUsageSummary = ({ term, interactionLimit, interactionDefinition, isNotStarted }) => {
  const safeLimit = interactionLimit ?? 0;
  const isUnlimited = !interactionLimit;
  const usageByChannel = Object.fromEntries((term.usage ?? []).map((entry) => [entry.channelType, entry]));

  const channels = Object.entries(CHANNEL_META).map(([channelType, meta]) => ({
    channelType,
    value: channelValue(usageByChannel[channelType], interactionDefinition),
    meta
  }));
  const usedTotal = channels.reduce((sum, c) => sum + c.value, 0);
  const isOverLimit = !isUnlimited && usedTotal > safeLimit;
  const usedColor = isOverLimit ? palette.error[500] : palette.text[800];

  if (isNotStarted) {
    return (
      <Stack alignItems="center" gap={1} sx={{ py: 5 }} data-testid="quota-not-started">
        <Typography variant="body1" color="text.700">Nothing here yet.</Typography>
        <Typography variant="body2" color="text.600" textAlign="center" maxWidth={320}>
          {QUOTA_NOT_STARTED_SUBTITLE}
        </Typography>
      </Stack>
    );
  }

  const Legend = (
    <Stack direction="row" gap={3} alignItems="center" sx={{ px: "12px", py: "6px" }}>
      {channels.map((c) => (
        <Stack
          key={c.channelType}
          direction="row"
          alignItems="center"
          gap="8px"
          data-testid={`quota-legend-${c.channelType}`}
        >
          <LegendDot color={c.meta.color} />
          <Typography variant="tooltip" sx={{ color: palette.text[600] }}>
            {c.meta.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack gap="18px" data-testid="quota-usage-summary">
      <Stack direction="row" alignItems="flex-end" gap="12px">
        <Stack direction="row" alignItems="center" gap="6px">
          <Typography
            variant="h5"
            sx={{ color: usedColor }}
            data-testid="quota-used-total"
            data-over-limit={isOverLimit ? "true" : undefined}
          >
            {usedTotal.toLocaleString()}
          </Typography>
          <Typography variant="h6" sx={{ color: palette.text[800] }}>
            /
          </Typography>
          <Typography variant="h5" sx={{ color: palette.text[700] }}>
            {isUnlimited ? UNLIMITED_LIMIT_LABEL : safeLimit.toLocaleString()}
          </Typography>
        </Stack>
        <Typography variant="subtitle2" sx={{ color: palette.text[700], lineHeight: "22px" }}>
          {ALL_CHANNELS_USAGE_LABEL}
        </Typography>
      </Stack>

      <Track>
        {usedTotal > 0 &&
          channels.map((c) =>
            c.value > 0 ? (
              <Tooltip
                key={c.channelType}
                title={tooltipFor(c.meta.label, c.meta.color, c.value, c.meta.tooltip)}
                variant="light"
                placement="top"
                stackStyle={{
                  flex: `${c.value} 1 0`,
                  minWidth: `${USAGE_BAR_MIN_SEGMENT_PX}px`,
                  height: "100%",
                  bgcolor: c.meta.color
                }}
              >
                <Box data-testid={`quota-bar-${c.channelType}`} sx={{ width: "100%", height: "100%" }} />
              </Tooltip>
            ) : null
          )}
        {safeLimit > usedTotal && (
          <Box
            data-testid="quota-bar-remaining"
            sx={{ flex: `${safeLimit - usedTotal} 1 0`, height: "100%" }}
          />
        )}
      </Track>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ color: palette.text[700], lineHeight: "22px" }}>
          {isUnlimited ? UNLIMITED_PLAN_SUBTITLE : buildPlanSubtitle(safeLimit)}
        </Typography>
        {Legend}
      </Stack>
    </Stack>
  );
};

export default QuotaUsageSummary;
